<template>
    <app-layout>
        <template #title>Slot debug: {{ eventType.name }}</template>

        <KioskNav />

        <div class="max-w-7xl px-4 py-8 sm:px-6 lg:px-8 mx-auto">
            <!-- Header -->
            <div class="mb-6 flex items-start justify-between">
                <div>
                    <h1 class="text-lg font-semibold text-iron-900">
                        Slot debug
                    </h1>
                    <p class="text-sm text-iron-500 mt-0.5">
                        {{ eventType.name }}
                        <span class="text-iron-300"> · </span>
                        {{ targetUser.name }}
                        <span class="text-iron-300">
                            ({{ targetUser.email }})
                        </span>
                    </p>
                </div>

                <inertia-link
                    :href="
                        route('app.kiosk.users.show', { user: targetUser.id })
                    "
                    class="px-3 py-1.5 text-xs font-medium rounded-md text-iron-600 hover:text-iron-800 hover:bg-iron-100 transition-colors"
                >
                    <ArrowLeft class="size-3 inline" /> Back to user
                </inertia-link>
            </div>

            <!-- Date range form -->
            <div
                class="bg-white rounded-xl border-iron-200 mb-4 overflow-hidden border"
            >
                <div class="px-5 py-4">
                    <form
                        class="gap-3 flex flex-wrap items-end"
                        @submit.prevent="reload"
                    >
                        <div class="w-44">
                            <label
                                for="from"
                                class="font-medium text-iron-400 tracking-wider mb-1 block text-[11px] uppercase"
                            >
                                From
                            </label>
                            <Input
                                id="from"
                                v-model="form.from"
                                type="date"
                                class="text-sm"
                            />
                        </div>
                        <div class="w-44">
                            <label
                                for="until"
                                class="font-medium text-iron-400 tracking-wider mb-1 block text-[11px] uppercase"
                            >
                                Until
                            </label>
                            <Input
                                id="until"
                                v-model="form.until"
                                type="date"
                                class="text-sm"
                            />
                        </div>
                        <Button size="sm" type="submit">Reload</Button>
                        <span class="text-xs text-iron-400 ml-2">
                            Times in user TZ
                            <span class="font-mono">{{
                                targetUser.timezone
                            }}</span>
                        </span>
                    </form>
                </div>
            </div>

            <!-- Setup card -->
            <div
                class="bg-white rounded-xl border-iron-200 mb-4 overflow-hidden border"
            >
                <div class="px-5 py-4">
                    <h3 class="text-sm font-semibold text-iron-900 mb-2">
                        Setup
                    </h3>
                    <div
                        v-if="eventTypeUser"
                        class="gap-x-8 gap-y-2 sm:grid-cols-3 text-xs grid grid-cols-2"
                    >
                        <div>
                            <p class="text-iron-400 text-[11px] uppercase">
                                Availability
                            </p>
                            <p class="text-iron-700">
                                {{ eventTypeUser.availability_name ?? "—"
                                }}<span
                                    v-if="eventTypeUser.availability_timezone"
                                    class="text-iron-400"
                                >
                                    /
                                    {{
                                        eventTypeUser.availability_timezone
                                    }}</span
                                >
                            </p>
                        </div>
                        <div>
                            <p class="text-iron-400 text-[11px] uppercase">
                                Calendar-defined availability
                            </p>
                            <p class="text-iron-700">
                                {{
                                    eventTypeUser.use_calendar_integration_availability
                                        ? `Yes (“${eventTypeUser.calendar_integration_availability_name}”)`
                                        : "No"
                                }}
                            </p>
                        </div>
                        <div>
                            <p class="text-iron-400 text-[11px] uppercase">
                                Conflict-check calendars
                            </p>
                            <p class="text-iron-700">
                                {{ conflictCalendarsSummary }}
                            </p>
                        </div>
                    </div>
                    <p v-else class="text-xs text-amber-700">
                        This user is not assigned to this event type (no
                        <code>event_type_user</code> row). Slots below show only
                        event-type level fallbacks.
                    </p>
                </div>
            </div>

            <!-- Legend -->
            <div
                class="bg-white rounded-xl border-iron-200 mb-4 overflow-hidden border"
            >
                <div
                    class="px-5 py-3 gap-4 text-xs flex flex-wrap items-center"
                >
                    <span class="gap-1.5 inline-flex items-center">
                        <span
                            class="size-3 rounded-sm bg-emerald-200 border-emerald-400 border"
                        />
                        Slot
                    </span>
                    <span class="gap-1.5 inline-flex items-center">
                        <span
                            class="size-3 rounded-sm bg-iron-200 border-iron-400 border"
                        />
                        Booked
                    </span>
                    <span class="gap-1.5 inline-flex items-center">
                        <span
                            class="size-3 rounded-sm bg-rose-200 border-rose-400 border"
                        />
                        Conflict
                    </span>
                    <span class="gap-1.5 inline-flex items-center">
                        <span
                            class="size-3 rounded-sm bg-blue-200 border-blue-400 border"
                        />
                        Availability marker
                    </span>
                    <span class="gap-1.5 inline-flex items-center">
                        <span
                            class="size-3 rounded-sm bg-iron-100 border-iron-300 border"
                        />
                        Transparent
                    </span>
                    <span
                        class="gap-1.5 text-iron-500 inline-flex items-center"
                    >
                        Errors fetching:
                        {{ errorCount }}
                    </span>
                </div>
            </div>

            <!-- Selected detail -->
            <div
                v-if="selected"
                class="bg-white rounded-xl border-iron-200 mb-4 overflow-hidden border"
            >
                <div class="px-5 py-3 gap-3 flex items-start justify-between">
                    <div class="flex-1">
                        <div
                            class="gap-2 mb-1 text-iron-400 tracking-wider flex items-center text-[10px] uppercase"
                        >
                            <span
                                v-if="selected.kind === 'event'"
                                class="font-semibold text-rose-700"
                            >
                                Calendar event
                            </span>
                            <span
                                v-else
                                class="font-semibold"
                                :class="
                                    selected.data.booked
                                        ? 'text-iron-500'
                                        : 'text-emerald-700'
                                "
                            >
                                {{
                                    selected.data.booked
                                        ? "Booked slot"
                                        : "Available slot"
                                }}
                            </span>
                            <span class="text-iron-400">
                                {{ selected.data.dayLabel }}
                            </span>
                        </div>
                        <div class="text-sm font-semibold text-iron-900">
                            <template v-if="selected.kind === 'event'">
                                {{ selected.data.summary || "(untitled)" }}
                            </template>
                            <template v-else>
                                {{ selected.data.timeLabel }}
                            </template>
                        </div>

                        <div
                            class="gap-x-6 gap-y-2 mt-3 sm:grid-cols-3 text-xs grid grid-cols-2"
                        >
                            <div>
                                <p
                                    class="text-iron-400 mb-0.5 text-[11px] uppercase"
                                >
                                    Time
                                </p>
                                <p class="text-iron-700 font-mono tabular-nums">
                                    {{ selected.data.timeLabel }}
                                </p>
                            </div>
                            <div>
                                <p
                                    class="text-iron-400 mb-0.5 text-[11px] uppercase"
                                >
                                    Start (ISO)
                                </p>
                                <p
                                    class="text-iron-700 font-mono text-[11px] break-all"
                                >
                                    {{ selected.data.start }}
                                </p>
                            </div>
                            <div>
                                <p
                                    class="text-iron-400 mb-0.5 text-[11px] uppercase"
                                >
                                    End (ISO)
                                </p>
                                <p
                                    class="text-iron-700 font-mono text-[11px] break-all"
                                >
                                    {{ selected.data.end }}
                                </p>
                            </div>

                            <template v-if="selected.kind === 'event'">
                                <div>
                                    <p
                                        class="text-iron-400 mb-0.5 text-[11px] uppercase"
                                    >
                                        Source
                                    </p>
                                    <p class="text-iron-700">
                                        <span class="font-mono">{{
                                            selected.data.source
                                        }}</span>
                                        <span
                                            v-if="
                                                selected.data.integration_name
                                            "
                                            class="text-iron-400"
                                        >
                                            ·
                                            {{ selected.data.integration_name }}
                                        </span>
                                    </p>
                                </div>
                                <div class="sm:col-span-2">
                                    <p
                                        class="text-iron-400 mb-0.5 text-[11px] uppercase"
                                    >
                                        Calendar
                                    </p>
                                    <p
                                        class="text-iron-700 font-mono text-[11px] break-all"
                                    >
                                        {{ selected.data.calendar_id ?? "—" }}
                                    </p>
                                </div>
                                <div class="sm:col-span-3">
                                    <p
                                        class="text-iron-400 mb-1 text-[11px] uppercase"
                                    >
                                        Flags
                                    </p>
                                    <div class="gap-1 flex flex-wrap">
                                        <span
                                            v-for="flag in eventFlags(
                                                selected.data,
                                            )"
                                            :key="flag"
                                            class="font-medium px-2 py-0.5 bg-iron-100 text-iron-700 inline-block rounded-full text-[11px]"
                                        >
                                            {{ flag }}
                                        </span>
                                        <span
                                            v-if="
                                                eventFlags(selected.data)
                                                    .length === 0
                                            "
                                            class="text-iron-400"
                                        >
                                            none
                                        </span>
                                    </div>
                                </div>
                                <div
                                    v-if="
                                        selected.data.raw &&
                                        Object.keys(selected.data.raw).length >
                                            0
                                    "
                                    class="sm:col-span-3 mt-1"
                                >
                                    <p
                                        class="text-iron-400 mb-1 text-[11px] uppercase"
                                    >
                                        Raw event data
                                    </p>
                                    <dl
                                        class="bg-iron-50 rounded-md gap-x-4 gap-y-1 px-3 py-2 grid grid-cols-[max-content_1fr] text-[11px]"
                                    >
                                        <template
                                            v-for="[key, value] in rawEntries(
                                                selected.data.raw,
                                            )"
                                            :key="key"
                                        >
                                            <dt
                                                class="font-mono text-iron-400 uppercase"
                                            >
                                                {{ key }}
                                            </dt>
                                            <dd
                                                :class="[
                                                    'text-iron-700 break-all',
                                                    key === 'busy_state'
                                                        ? value === 'busy'
                                                            ? 'font-semibold text-rose-700'
                                                            : 'font-semibold text-emerald-700'
                                                        : '',
                                                ]"
                                            >
                                                <a
                                                    v-if="isUrl(value)"
                                                    :href="value"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    class="text-blue-600 hover:underline"
                                                >
                                                    {{ value }}
                                                </a>
                                                <span v-else>{{ value }}</span>
                                            </dd>
                                        </template>
                                    </dl>
                                </div>
                            </template>

                            <template v-else>
                                <div>
                                    <p
                                        class="text-iron-400 mb-0.5 text-[11px] uppercase"
                                    >
                                        Status
                                    </p>
                                    <p class="text-iron-700">
                                        {{
                                            selected.data.booked
                                                ? "Booked"
                                                : "Available"
                                        }}
                                    </p>
                                </div>
                            </template>
                        </div>
                    </div>

                    <button
                        type="button"
                        class="text-iron-400 hover:text-iron-700 transition-colors"
                        title="Close (Esc)"
                        @click="clearSelection"
                    >
                        <X class="size-4" />
                    </button>
                </div>
            </div>

            <!-- Calendar grid -->
            <div
                class="bg-white rounded-xl border-iron-200 overflow-hidden border"
            >
                <div class="overflow-x-auto">
                    <div
                        class="calendar-grid"
                        :style="{
                            '--row-h': `${ROW_HEIGHT}px`,
                            '--start-h': START_HOUR,
                            '--end-h': END_HOUR,
                        }"
                    >
                        <!-- Header row -->
                        <div class="header-cell" />
                        <div
                            v-for="day in days"
                            :key="`h-${day.date}`"
                            class="header-cell day-header"
                        >
                            <div
                                class="text-iron-400 tracking-wider text-[10px] uppercase"
                            >
                                {{ day.weekdayLabel }}
                            </div>
                            <div
                                class="font-semibold text-iron-900 text-sm tabular-nums"
                            >
                                {{ day.dayLabel }}
                            </div>
                        </div>

                        <!-- Hour axis -->
                        <div class="hour-axis">
                            <div
                                v-for="hour in hourLabels"
                                :key="`hr-${hour}`"
                                class="hour-label"
                            >
                                {{ formatHour(hour) }}
                            </div>
                        </div>

                        <!-- Day columns -->
                        <div
                            v-for="day in days"
                            :key="`c-${day.date}`"
                            class="day-column"
                        >
                            <!-- Hour grid lines -->
                            <div
                                v-for="hour in hourLines"
                                :key="`gl-${day.date}-${hour}`"
                                class="hour-line"
                                :style="{
                                    '--top': `${(hour - START_HOUR) * ROW_HEIGHT}px`,
                                }"
                            />

                            <!-- Calendar events (left half) -->
                            <button
                                v-for="(ev, idx) in day.events"
                                :key="`e-${day.date}-${idx}`"
                                type="button"
                                :class="[
                                    'event-block',
                                    eventClass(ev),
                                    isSelectedEvent(ev) && 'is-selected',
                                ]"
                                :style="{
                                    '--top': `${ev.topPx}px`,
                                    '--h': `${ev.heightPx}px`,
                                }"
                                :title="eventTooltip(ev)"
                                @click="selectEvent(ev)"
                            >
                                <div class="event-time tabular-nums">
                                    {{ ev.timeLabel }}
                                </div>
                                <div class="event-summary">
                                    {{ ev.summary || "(untitled)" }}
                                </div>
                            </button>

                            <!-- Slots (right half) -->
                            <button
                                v-for="(slot, idx) in day.slots"
                                :key="`s-${day.date}-${idx}`"
                                type="button"
                                :class="[
                                    'slot-block',
                                    slot.booked
                                        ? 'bg-iron-100 text-iron-500 border-iron-300'
                                        : 'bg-emerald-50 text-emerald-700 border-emerald-300',
                                    isSelectedSlot(slot) && 'is-selected',
                                ]"
                                :style="{
                                    '--top': `${slot.topPx}px`,
                                    '--h': `${slot.heightPx}px`,
                                }"
                                :title="`${slot.timeLabel}${slot.booked ? ' · booked' : ''}`"
                                @click="selectSlot(slot)"
                            >
                                <span class="tabular-nums">{{
                                    slot.startLabel
                                }}</span>
                            </button>

                            <!-- Out-of-range badges -->
                            <div
                                v-if="day.beforeRange > 0"
                                class="out-of-range-top"
                                :title="`${day.beforeRange} earlier today`"
                            >
                                +{{ day.beforeRange }} earlier
                            </div>
                            <div
                                v-if="day.afterRange > 0"
                                class="out-of-range-bottom"
                                :title="`${day.afterRange} later today`"
                            >
                                +{{ day.afterRange }} later
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Errors block (rare, shown at the bottom if any fetch failed) -->
            <div
                v-if="errorEvents.length > 0"
                class="bg-rose-50 border-rose-200 rounded-xl mt-4 px-5 py-3 text-xs border"
            >
                <p class="font-semibold text-rose-800 mb-1">
                    Calendar fetch errors
                </p>
                <ul role="list" class="space-y-1 text-rose-700">
                    <li v-for="(ev, idx) in errorEvents" :key="`err-${idx}`">
                        <span class="font-mono">{{ ev.source }}</span>
                        ·
                        {{ ev.calendar_id }}
                        — {{ ev.error }}
                    </li>
                </ul>
            </div>
        </div>
    </app-layout>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { router } from "@inertiajs/vue3";
import AppLayout from "../../../../Layouts/AppLayout.vue";
import KioskNav from "../components/KioskNav.vue";
import { Input } from "../../../../components/ui/input";
import { Button } from "../../../../components/ui/button";
import { ArrowLeft, X } from "lucide-vue-next";
import route from "ziggy-js";

interface Slot {
    start: string;
    end: string;
    booked: boolean;
}

interface CalendarEvent {
    source: string;
    summary?: string;
    start?: string;
    end?: string;
    all_day?: boolean;
    transparent?: boolean;
    is_availability_definition?: boolean;
    counts_as_conflict?: boolean;
    calendar_id?: string;
    integration_name?: string;
    raw?: Record<string, string>;
    error?: string;
}

interface EventTypeUserSetup {
    use_calendar_integration_availability: boolean;
    calendar_integration_availability_name: string | null;
    use_availability: boolean;
    availability_name: string | null;
    availability_timezone: string | null;
    google_calendars: Array<Record<string, unknown>>;
    i_cloud_calendars: Array<Record<string, unknown>>;
    conflict_check_calendars: Array<Record<string, unknown>> | null;
}

const props = defineProps<{
    eventType: {
        id: number;
        name: string;
        slug: string | null;
        duration: number | null;
        team_name: string;
    };
    targetUser: {
        id: number;
        name: string;
        email: string;
        timezone: string;
    };
    eventTypeUser: EventTypeUserSetup | null;
    from: string;
    until: string;
    slots: Slot[];
    calendarEvents: CalendarEvent[];
}>();

const START_HOUR = 6;
const END_HOUR = 22;
const ROW_HEIGHT = 32;
const VISIBLE_MINUTES = (END_HOUR - START_HOUR) * 60;

const form = ref({
    from: props.from.slice(0, 10),
    until: props.until.slice(0, 10),
});

function reload() {
    router.get(
        route("app.kiosk.event-types.slot-debug", {
            eventType: props.eventType.id,
            user: props.targetUser.id,
        }),
        { from: form.value.from, until: form.value.until },
        { preserveState: false, preserveScroll: false },
    );
}

const tzFormatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: props.targetUser.timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
});

function dateKey(iso: string | undefined): string {
    if (!iso) {
        return "";
    }
    return tzFormatter.format(new Date(iso));
}

function tzParts(iso: string): { hour: number; minute: number } {
    const parts = new Intl.DateTimeFormat("en-GB", {
        timeZone: props.targetUser.timezone,
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    }).formatToParts(new Date(iso));
    const hour = Number(parts.find((p) => p.type === "hour")?.value ?? "0");
    const minute = Number(parts.find((p) => p.type === "minute")?.value ?? "0");
    return { hour, minute };
}

function minutesFromMidnight(iso: string): number {
    const { hour, minute } = tzParts(iso);
    return hour * 60 + minute;
}

function formatTime(iso: string | undefined): string {
    if (!iso) {
        return "—";
    }
    return new Intl.DateTimeFormat("en-GB", {
        timeZone: props.targetUser.timezone,
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    }).format(new Date(iso));
}

function formatHour(hour: number): string {
    return `${String(hour).padStart(2, "0")}:00`;
}

function pxFromMinutes(minutes: number): number {
    const visibleStart = START_HOUR * 60;
    const clamped = Math.max(
        0,
        Math.min(VISIBLE_MINUTES, minutes - visibleStart),
    );
    return (clamped * ROW_HEIGHT) / 60;
}

function eventClass(ev: PositionedEvent): string {
    if (ev.is_availability_definition) {
        return "bg-blue-100/70 border-blue-300 text-blue-900";
    }
    if (ev.transparent) {
        return "bg-iron-100/70 border-iron-300 text-iron-600";
    }
    if (ev.counts_as_conflict) {
        return "bg-rose-100/70 border-rose-300 text-rose-900";
    }
    return "bg-iron-100/70 border-iron-300 text-iron-700";
}

function eventTooltip(ev: PositionedEvent): string {
    const flags: string[] = [];
    if (ev.transparent) flags.push("transparent");
    if (ev.is_availability_definition) flags.push("availability marker");
    if (ev.counts_as_conflict) flags.push("blocks slots");
    return `${ev.timeLabel}\n${ev.summary || "(untitled)"}\n${ev.source} · ${ev.calendar_id ?? ""}\n${flags.join(" · ")}`;
}

interface PositionedSlot {
    booked: boolean;
    timeLabel: string;
    startLabel: string;
    dayLabel: string;
    start: string;
    end: string;
    topPx: number;
    heightPx: number;
}

interface PositionedEvent {
    summary?: string;
    source: string;
    integration_name?: string;
    calendar_id?: string;
    timeLabel: string;
    dayLabel: string;
    start: string;
    end: string;
    all_day?: boolean;
    transparent?: boolean;
    is_availability_definition?: boolean;
    counts_as_conflict?: boolean;
    raw?: Record<string, string>;
    topPx: number;
    heightPx: number;
}

interface DayBucket {
    date: string;
    weekdayLabel: string;
    dayLabel: string;
    slots: PositionedSlot[];
    events: PositionedEvent[];
    beforeRange: number;
    afterRange: number;
}

const errorEvents = computed(() => props.calendarEvents.filter((e) => e.error));

const errorCount = computed(() => errorEvents.value.length);

type Selection =
    | { kind: "event"; data: PositionedEvent }
    | { kind: "slot"; data: PositionedSlot }
    | null;

const selected = ref<Selection>(null);

function selectEvent(ev: PositionedEvent): void {
    selected.value = { kind: "event", data: ev };
}

function selectSlot(slot: PositionedSlot): void {
    selected.value = { kind: "slot", data: slot };
}

function clearSelection(): void {
    selected.value = null;
}

function isSelectedEvent(ev: PositionedEvent): boolean {
    return selected.value?.kind === "event" && selected.value.data === ev;
}

function isSelectedSlot(slot: PositionedSlot): boolean {
    return selected.value?.kind === "slot" && selected.value.data === slot;
}

function eventFlags(ev: PositionedEvent): string[] {
    const flags: string[] = [];
    if (ev.transparent) flags.push("transparent");
    if (ev.is_availability_definition) flags.push("availability marker");
    if (ev.counts_as_conflict) flags.push("blocks slots");
    if (ev.all_day) flags.push("all-day");
    return flags;
}

const RAW_KEY_ORDER = [
    "busy_state",
    "status",
    "transparency",
    "visibility",
    "class",
    "event_type",
    "organizer",
    "attendee_count",
    "attendees",
    "location",
    "description",
    "recurrence",
    "rrule",
    "recurring_event_id",
    "hangout_link",
    "conference_url",
    "html_link",
    "timezone",
    "created",
    "updated",
    "last_modified",
    "ical_uid",
    "uid",
    "event_id",
];

function rawEntries(raw: Record<string, string>): Array<[string, string]> {
    const entries = Object.entries(raw);
    return entries.sort(([a], [b]) => {
        const ai = RAW_KEY_ORDER.indexOf(a);
        const bi = RAW_KEY_ORDER.indexOf(b);
        if (ai === -1 && bi === -1) return a.localeCompare(b);
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
    });
}

function isUrl(value: string): boolean {
    return /^https?:\/\//.test(value);
}

function onKeydown(e: KeyboardEvent): void {
    if (e.key === "Escape") {
        clearSelection();
    }
}

onMounted(() => {
    window.addEventListener("keydown", onKeydown);
});

onBeforeUnmount(() => {
    window.removeEventListener("keydown", onKeydown);
});

const days = computed<DayBucket[]>(() => {
    const map = new Map<string, DayBucket>();

    const fromDate = new Date(props.from);
    const untilDate = new Date(props.until);
    const cursor = new Date(fromDate);
    cursor.setHours(0, 0, 0, 0);

    while (cursor <= untilDate) {
        const key = tzFormatter.format(cursor);
        const weekday = new Intl.DateTimeFormat("en-GB", {
            timeZone: props.targetUser.timezone,
            weekday: "short",
        }).format(cursor);
        const dayLabel = new Intl.DateTimeFormat("en-GB", {
            timeZone: props.targetUser.timezone,
            day: "2-digit",
            month: "short",
        }).format(cursor);
        if (!map.has(key)) {
            map.set(key, {
                date: key,
                weekdayLabel: weekday,
                dayLabel,
                slots: [],
                events: [],
                beforeRange: 0,
                afterRange: 0,
            });
        }
        cursor.setDate(cursor.getDate() + 1);
    }

    for (const slot of props.slots) {
        if (!slot.start || !slot.end) {
            continue;
        }
        const key = dateKey(slot.start);
        const bucket = map.get(key);
        if (!bucket) {
            continue;
        }

        const startMin = minutesFromMidnight(slot.start);
        const endMin = minutesFromMidnight(slot.end);
        const visibleStart = START_HOUR * 60;
        const visibleEnd = END_HOUR * 60;

        if (endMin <= visibleStart) {
            bucket.beforeRange += 1;
            continue;
        }
        if (startMin >= visibleEnd) {
            bucket.afterRange += 1;
            continue;
        }

        bucket.slots.push({
            booked: slot.booked,
            timeLabel: `${formatTime(slot.start)}–${formatTime(slot.end)}`,
            startLabel: formatTime(slot.start),
            dayLabel: bucket.dayLabel,
            start: slot.start,
            end: slot.end,
            topPx: pxFromMinutes(startMin),
            heightPx: Math.max(
                12,
                pxFromMinutes(endMin) - pxFromMinutes(startMin),
            ),
        });
    }

    for (const ev of props.calendarEvents) {
        if (ev.error || !ev.start || !ev.end) {
            continue;
        }
        const key = dateKey(ev.start);
        const bucket = map.get(key);
        if (!bucket) {
            continue;
        }

        const startMin = minutesFromMidnight(ev.start);
        const rawEndMin = minutesFromMidnight(ev.end);
        const endMin = rawEndMin <= startMin ? END_HOUR * 60 : rawEndMin;
        const visibleStart = START_HOUR * 60;
        const visibleEnd = END_HOUR * 60;

        if (endMin <= visibleStart || startMin >= visibleEnd) {
            continue;
        }

        bucket.events.push({
            summary: ev.summary,
            source: ev.source,
            integration_name: ev.integration_name,
            calendar_id: ev.calendar_id,
            timeLabel: ev.all_day
                ? "all day"
                : `${formatTime(ev.start)}–${formatTime(ev.end)}`,
            dayLabel: bucket.dayLabel,
            start: ev.start,
            end: ev.end,
            all_day: ev.all_day,
            transparent: ev.transparent,
            is_availability_definition: ev.is_availability_definition,
            counts_as_conflict: ev.counts_as_conflict,
            raw: ev.raw,
            topPx: pxFromMinutes(startMin),
            heightPx: Math.max(
                14,
                pxFromMinutes(endMin) - pxFromMinutes(startMin),
            ),
        });
    }

    for (const bucket of map.values()) {
        bucket.slots.sort((a, b) => a.topPx - b.topPx);
        bucket.events.sort((a, b) => a.topPx - b.topPx);
    }

    return Array.from(map.values());
});

const hourLabels = computed(() => {
    const out: number[] = [];
    for (let h = START_HOUR; h <= END_HOUR; h++) {
        out.push(h);
    }
    return out;
});

const hourLines = computed(() => {
    const out: number[] = [];
    for (let h = START_HOUR + 1; h < END_HOUR; h++) {
        out.push(h);
    }
    return out;
});

const conflictCalendarsSummary = computed(() => {
    if (!props.eventTypeUser) {
        return "—";
    }
    const parts: string[] = [];
    const google = props.eventTypeUser.google_calendars?.length ?? 0;
    const icloud = props.eventTypeUser.i_cloud_calendars?.length ?? 0;
    const extras = props.eventTypeUser.conflict_check_calendars?.length ?? 0;
    if (google) {
        parts.push(`Google: ${google}`);
    }
    if (icloud) {
        parts.push(`iCloud: ${icloud}`);
    }
    if (extras) {
        parts.push(`extras: ${extras}`);
    }
    return parts.length ? parts.join(", ") : "none";
});
</script>

<style scoped>
.calendar-grid {
    display: grid;
    grid-template-columns: 56px repeat(
            v-bind("days.length"),
            minmax(140px, 1fr)
        );
    grid-auto-rows: auto;
    min-width: max-content;
}

.header-cell {
    background: white;
    border-bottom: 1px solid #f4f4f5;
    padding: 0.5rem 0.5rem;
    position: sticky;
    top: 0;
    z-index: 2;
}

.day-header {
    border-left: 1px solid #f4f4f5;
}

.hour-axis {
    position: relative;
    height: calc(var(--row-h) * (var(--end-h) - var(--start-h)));
    border-right: 1px solid #f4f4f5;
}

.hour-label {
    height: var(--row-h);
    padding-right: 6px;
    font-family: ui-monospace, SFMono-Regular, monospace;
    font-size: 0.6875rem;
    color: #a0a0ab;
    text-align: right;
    line-height: 1;
    transform: translateY(-0.4em);
}

.day-column {
    position: relative;
    height: calc(var(--row-h) * (var(--end-h) - var(--start-h)));
    border-left: 1px solid #f4f4f5;
    background-image: linear-gradient(
        to bottom,
        transparent calc(var(--row-h) - 1px),
        #fafafa calc(var(--row-h) - 1px),
        #fafafa var(--row-h)
    );
    background-size: 100% var(--row-h);
}

.hour-line {
    position: absolute;
    left: 0;
    right: 0;
    top: var(--top);
    border-top: 1px dashed transparent;
    pointer-events: none;
}

.event-block {
    position: absolute;
    top: var(--top);
    height: var(--h);
    left: 2px;
    right: 50%;
    margin-right: 2px;
    border-radius: 4px;
    border-width: 1px;
    padding: 2px 4px;
    overflow: hidden;
    font-size: 0.6875rem;
    line-height: 1.1;
    text-align: left;
    cursor: pointer;
    transition: filter 120ms ease;
}

.event-block:hover {
    filter: brightness(0.96);
}

.event-block.is-selected,
.slot-block.is-selected {
    box-shadow: 0 0 0 2px #18181b;
    z-index: 1;
}

.event-time {
    font-family: ui-monospace, SFMono-Regular, monospace;
    font-size: 0.625rem;
    opacity: 0.7;
}

.event-summary {
    font-weight: 500;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
}

.slot-block {
    position: absolute;
    top: var(--top);
    height: var(--h);
    left: 50%;
    right: 2px;
    margin-left: 2px;
    border-radius: 4px;
    border-width: 1px;
    padding: 2px 4px;
    overflow: hidden;
    font-size: 0.6875rem;
    line-height: 1.1;
    text-align: left;
    cursor: pointer;
    transition: filter 120ms ease;
}

.slot-block:hover {
    filter: brightness(0.96);
}

.out-of-range-top,
.out-of-range-bottom {
    position: absolute;
    left: 4px;
    right: 4px;
    font-size: 0.625rem;
    color: #a0a0ab;
    text-align: center;
    padding: 1px 2px;
    background: white;
    border: 1px dashed #e4e4e7;
    border-radius: 4px;
}

.out-of-range-top {
    top: 2px;
}

.out-of-range-bottom {
    bottom: 2px;
}
</style>
